<script setup lang="ts">
import type { BankAccount, ManualBankAccountType } from '@/types/UserData';
import { joinDigit } from '@/utils/bank-account-labels';

type Props = {
  editAccount?: BankAccount | null;
  // Minha Conta V2 (ScreenBankAccounts.vue, via PayerAccounts.vue) embute
  // este modal, mas quer a cor/raio na paleta v2 (escura), não o azul do
  // site antigo.
  variant?: 'default' | 'v2';
};

const { editAccount = null, variant = 'default' } = defineProps<Props>();

const emit = defineEmits<{ saved: [] }>();

const isVisible = defineModel<boolean>('modelValue', { default: false });

// Bug real: sem lock nenhum, dava pra rolar o conteúdo atrás deste modal no
// mobile (ver useModalScrollLock).
useModalScrollLock(isVisible);

const { ApiService } = useApiService();
const { openToaster } = useLayoutStore();
const { t } = useTexts();
const { fetchUserProfile } = useUserData();
const { $containment } = useNuxtApp();

const bankId = ref<number | ''>('');
const agency = ref('');
const accountNumber = ref('');
const accountType = ref<ManualBankAccountType | ''>('');
const typePix = ref('');
const keyPix = ref('');
const isSaving = ref(false);

const isEditing = computed(() => !!editAccount);

const isFormValid = computed(
  () =>
    !!bankId.value &&
    !!agency.value &&
    !!accountNumber.value &&
    !!accountType.value &&
    !!typePix.value &&
    !!keyPix.value &&
    isValidAgencyOrAccountNumber(agency.value) &&
    isValidAgencyOrAccountNumber(accountNumber.value) &&
    (typePix.value !== 'RANDOM' || isValidRandomPixKey(keyPix.value)) &&
    (typePix.value !== 'PHONE' || isValidPhonePixKey(keyPix.value)) &&
    (typePix.value !== 'EMAIL' || isEmailValid(keyPix.value)),
);

// Telefone tem máscara (v-maska em AccountFormFields) — keyPix.value fica
// com pontuação ("+55 (19) 99711-9007"), mas o backend e editAccount.key_pix
// só têm dígitos. Compara/envia sempre a versão sem máscara pro telefone.
function unmaskKeyPix(value: string) {
  return typePix.value === 'PHONE' ? value.replace(/\D/g, '') : value;
}

// Edição sem alterar nada: manter o botão liberado disparava um PATCH
// idêntico (e reabria o fluxo de senha/KYC à toa). Compara contra os
// mesmos valores que resetForm() usou pra preencher o form.
const hasChanges = computed(() => {
  if (!isEditing.value) return true;

  return (
    bankId.value !== (editAccount?.bank_id ?? '') ||
    agency.value !==
      joinDigit(editAccount?.agency, editAccount?.agency_digit) ||
    accountNumber.value !==
      joinDigit(editAccount?.number, editAccount?.number_digit) ||
    accountType.value !==
      ((editAccount?.type as ManualBankAccountType) ?? '') ||
    typePix.value !== (editAccount?.type_pix ?? '') ||
    unmaskKeyPix(keyPix.value) !== (editAccount?.key_pix ?? '')
  );
});

// "12345-6" → dígito verificador do banco (agência/conta), separado do
// número principal pro payload — ver ManualAccountModal §onSaveClick.
function splitDigit(value: string): [main: string, digit: string] {
  const dashIndex = value.lastIndexOf('-');

  if (dashIndex === -1) return [value, ''];

  return [value.slice(0, dashIndex), value.slice(dashIndex + 1)];
}

function resetForm() {
  // $containment.handle() só chama o callback (saveAccount) depois de um
  // possível modal de senha/KYC ser resolvido — se o jogador cancelar esse
  // modal, saveAccount() nunca roda e isSaving ficaria travado em `true`.
  // Fechar/reabrir este modal (o único jeito de tentar de novo) libera.
  isSaving.value = false;
  bankId.value = editAccount?.bank_id ?? '';
  agency.value = joinDigit(editAccount?.agency, editAccount?.agency_digit);
  accountNumber.value = joinDigit(
    editAccount?.number,
    editAccount?.number_digit,
  );
  // BankAccount.type cobre os dois enums (legado + manual) porque a mesma
  // conta pode ter sido criada por qualquer um dos fluxos; o valor é a
  // mesma string em ambos os casos (ex.: "CORRENTE"), só o tipo nominal do
  // TS diverge.
  accountType.value = (editAccount?.type as ManualBankAccountType) ?? '';
  // key_pix/type_pix não são digitados aqui — AccountFormFields deriva o
  // valor do perfil do jogador assim que typePix é setado (ver keyPixOptions).
  typePix.value = editAccount?.type_pix ?? '';
  keyPix.value = editAccount?.key_pix ?? '';
}

function closeModal() {
  isVisible.value = false;
  resetForm();
}

async function onSaveClick() {
  if (!isFormValid.value || isSaving.value) return;

  isSaving.value = true;

  // Contrato BFF §5 / Guia PIX §5: POST/PATCH bank-account passam por
  // validateSensitiveRoute — o país pode exigir password_check/kyc_id
  // (grupo "update_data"). Mesmo padrão de BankAccounts.vue (fluxo PIX).
  await $containment.handle('update_data', () => {
    saveAccount();
  });
}

async function saveAccount() {
  const { $kyc } = useNuxtApp();
  const { temporaryPassword } = useModulesValidation();

  const [agencyNumber, agencyDigit] = splitDigit(agency.value);
  const [accountNumberMain, accountDigit] = splitDigit(accountNumber.value);

  const payload = {
    key_pix: unmaskKeyPix(keyPix.value),
    type_pix: typePix.value,
    bank_id: bankId.value,
    agency: agencyNumber,
    // Contrato BFF §3: agency_digit/number_digit são `nullable` — sem
    // dígito verificador manda null, não string vazia (splitDigit devolve
    // '' quando não tem hífen).
    agency_digit: agencyDigit || null,
    number: accountNumberMain,
    number_digit: accountDigit || null,
    type: accountType.value,
    ...($kyc?.current?.id ? { kyc_id: $kyc.current.id } : {}),
    ...(temporaryPassword.get()
      ? { password_check: temporaryPassword.get() }
      : {}),
  };

  try {
    if (isEditing.value) {
      await ApiService.patch(
        `/bff/users/bank-account/${editAccount!.id}`,
        payload,
      );
    } else {
      await ApiService.post('/bff/users/bank-account', payload);
    }

    fetchUserProfile({ force: true }).catch((err) => {
      console.error('manual-account-modal: refreshing profile', err);
    });

    openToaster({
      message: isEditing.value
        ? t('bank_account.manual_modal.update_success')
        : t('bank_account.manual_modal.save_success'),
      type: 'success',
    });

    emit('saved');
    closeModal();
  } catch (err: any) {
    console.error('manual-account-modal: saving', err);

    openToaster({
      message: getBankAccountErrorMessage(
        err,
        t,
        t('bank_account.manual_modal.save_error'),
      ),
      type: 'error',
    });
  } finally {
    isSaving.value = false;
    temporaryPassword.clear();
  }
}

// abre já preenchido quando é edição (editAccount vem de fora antes do open)
watch(isVisible, (open) => {
  if (open) resetForm();
});
</script>

<template>
  <BaseModal
    v-model="isVisible"
    :aria-label="
      isEditing
        ? $t('bank_account.manual_modal.edit_title')
        : $t('bank_account.manual_modal.title')
    "
    :custom-class-wrapper="
      variant === 'v2'
        ? `${$style.wrapper} ${$style.wrapperV2}`
        : $style.wrapper
    "
    hide-close-button
    :mobile-fullscreen="false"
    :width="366"
    @close="resetForm"
  >
    <div :class="$style.content">
      <div :class="$style.header">
        <button
          :class="$style.btnClose"
          data-autofocus-skip
          type="button"
          @click="closeModal"
        >
          {{ $t('bank_account.manual_modal.close') }}
        </button>
      </div>

      <h2 :class="$style.title">
        {{
          isEditing
            ? $t('bank_account.manual_modal.edit_title')
            : $t('bank_account.manual_modal.title')
        }}
      </h2>
      <p :class="$style.subtitle">
        {{ $t('bank_account.manual_modal.subtitle') }}
      </p>

      <BankAccountFormFields
        v-model:account-number="accountNumber"
        v-model:account-type="accountType"
        v-model:agency="agency"
        v-model:bank-id="bankId"
        v-model:key-pix="keyPix"
        v-model:type-pix="typePix"
      />

      <button
        :class="[$style.btnSave, { [$style.btnSaveV2]: variant === 'v2' }]"
        :disabled="!isFormValid || isSaving || !hasChanges"
        type="button"
        @click="onSaveClick"
      >
        {{ $t('bank_account.manual_modal.save') }}
      </button>
    </div>
  </BaseModal>
</template>

<style module lang="postcss">
.wrapper {
  @apply !bg-bg-secondary !rounded-2xl !border !border-solid !border-primary/25;

  /* mesmo fundo/borda escuros do painel v2, no lugar do azul do site antigo */
  &.wrapperV2 {
    @apply !rounded-[20px] !border-v2-default/40 !bg-v2-background;
  }
}

.content {
  @apply flex flex-col pt-4 px-4 pb-5;
}

.header {
  @apply flex justify-end;
}

.btnClose {
  @apply min-h-11 px-1.5 bg-transparent text-texts/70 text-xs font-bold tracking-[0.6px];
}

.title {
  @apply -mt-1.5 mb-0.5 text-base font-bold text-texts;
}

.subtitle {
  @apply text-texts/60 text-xs leading-[1.5];
}

.btnSave {
  @apply w-full h-12 mt-6 rounded-lg bg-primary text-bg-secondary text-sm font-bold tracking-[0.6px];

  &:disabled {
    @apply opacity-[0.35] cursor-default;
  }

  /* mesmo botão primário escuro do resto do painel v2 */
  &.btnSaveV2 {
    @apply h-[40px] rounded-[12px] bg-v2-primary text-[14px] font-normal tracking-normal text-v2-primary-foreground;
  }
}
</style>
